import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import type {
  TActiveFilter,
  TCampaignType,
  TInfluencerAward,
  TInfluencerStatus,
  TPendingAmbassadorSource,
  TPunishmentType,
} from "@/store/types";
import useUserStore from "@/store/user";
import qs from "qs";

export type TLocationSearchItem = {
  country: string;
  country_code?: string;
  name: string;
  place_name?: string;
  type: "city" | "region" | "zip";
};

export const FILTER_ORDERING_KEYS = ["favourites_first", "ordering", "recommended"] as const;
export const FILTER_PROFILE_LEVEL_KEYS = [
  "feed_hashtags",
  "feed_hashtags_mode",
  "feed_tags",
  "feed_tags_mode",
] as const;
export const FILTER_PRESERVED_ON_MERGE_KEYS = [...FILTER_ORDERING_KEYS, ...FILTER_PROFILE_LEVEL_KEYS] as const;
export const FILTER_UNSAVEABLE_KEYS = [
  ...FILTER_ORDERING_KEYS,
  ...FILTER_PROFILE_LEVEL_KEYS,
  "cutoff_value",
  "maximum_cutoff_value",
];

export type TFilters = {
  age_range_start: number[];
  audience_country: string[];
  audience_gender: string | null;
  audience_location_search: TLocationSearchItem[];
  award: TInfluencerAward | null;
  clothing_below: string[];
  clothing_shoe_size: string[];
  clothing_top: string[];
  cutoff_value: number;
  maximum_cutoff_value: number;
  engagement_end: number;
  engagement_start: number;
  favourites_first: boolean | null;
  feed_hashtags: string[];
  feed_hashtags_mode: "include" | "exclude";
  feed_tags: string[];
  feed_tags_mode: "include" | "exclude";
  followers_end: number;
  followers_start: number;
  gender: string[];
  group_ids: number[];
  group_membership: "in_group" | "not_in_group" | null;
  influencer_age_range_start: number[];
  influencer_country: string[];
  influencer_interests: number[];
  is_competitor_filter_active: boolean;
  location_radius_km: number;
  location_search: TLocationSearchItem[];
  median_views_end: number;
  median_views_start: number;
  ordering:
    | "-created"
    | "-engagement"
    | "-followers"
    | "-median_views"
    | "-rating"
    | "-recommended"
    | "-user__last_app_login"
    | "favourites_first"
    | null;
  new: boolean;
  new_on_platform: boolean;
  no_review: boolean;
  punishment_type: TPunishmentType | null;
  rating_end: number;
  rating_start: number;
  recommended: boolean | null;
  search: string;
  source: TPendingAmbassadorSource | null;
  status: TInfluencerStatus[];
  tiktok_last_period_video_count_end: number;
  tiktok_last_period_video_count_start: number;
  type: TCampaignType;
  vibe: string;
};

export type TInitialFilters = {
  ages: number[];
  cities: {country__name: string; name: string}[];
  count: number;
  engagement__histogram: {range: number; value: number}[];
  engagement__max: number;
  engagement__min: number;
  followers__histogram: {range: number; value: number}[];
  followers__max: number;
  followers__min: number;
  median_views__histogram: {range: number; value: number}[];
  median_views__max: number;
  median_views__min: number;
  rating__histogram: {range: number; value: number}[];
  rating__max: number;
  rating__min: number;
  tiktok_last_period_video_count__histogram: {range: number; value: number}[];
  tiktok_last_period_video_count__max: number;
  tiktok_last_period_video_count__min: number;
};

export const DEFAULT_FILTERS: TFilters = {
  age_range_start: [],
  audience_country: [],
  audience_gender: null,
  audience_location_search: [],
  award: null,
  clothing_below: [],
  clothing_shoe_size: [],
  clothing_top: [],
  cutoff_value: 3,
  maximum_cutoff_value: 1,
  engagement_end: 0,
  engagement_start: 0,
  favourites_first: null,
  feed_hashtags: [],
  feed_hashtags_mode: "exclude",
  feed_tags: [],
  feed_tags_mode: "exclude",
  followers_end: 0,
  followers_start: 0,
  gender: [],
  group_ids: [],
  group_membership: null,
  influencer_age_range_start: [],
  influencer_country: [],
  influencer_interests: [],
  is_competitor_filter_active: false,
  location_radius_km: 0,
  location_search: [],
  median_views_end: 0,
  median_views_start: 0,
  new: false,
  new_on_platform: false,
  no_review: false,
  ordering: null,
  punishment_type: null,
  rating_end: 5,
  rating_start: 0,
  recommended: null,
  search: "",
  source: null,
  status: [],
  tiktok_last_period_video_count_end: 0,
  tiktok_last_period_video_count_start: 0,
  type: "instagram",
  vibe: "",
};

export const MAX_CUTOFF = 5;

export const DEFAULT_INITIAL_FILTERS: TInitialFilters = {
  ages: [],
  cities: [],
  count: 0,
  engagement__histogram: [],
  engagement__max: 0,
  engagement__min: 0,
  followers__histogram: [],
  followers__max: 0,
  followers__min: 0,
  median_views__histogram: [],
  median_views__max: 0,
  median_views__min: 0,
  rating__histogram: [],
  rating__max: 5,
  rating__min: 0,
  tiktok_last_period_video_count__histogram: [],
  tiktok_last_period_video_count__max: 0,
  tiktok_last_period_video_count__min: 0,
};

/**
 * Determines which vibe search action button to show based on frequency distribution.
 * Frequency distribution values are cumulative, so the number of new results at a given
 * cutoff is the difference between that cutoff's value and the current cutoff's value.
 * - "similar" if the current level has >= 12 results, the next level adds more,
 *   and the next level is not the final cutoff
 * - "broaden" if any later level adds more results and "similar" doesn't apply
 * - "none" if no later level adds any new results
 */
export const getVibeSearchAction = (
  cutoffValue: number,
  frequencyDistribution: Record<number, number>,
): "broaden" | "none" | "similar" => {
  const currentCount = frequencyDistribution[cutoffValue] ?? 0;
  const nextCount = frequencyDistribution[cutoffValue + 1] ?? currentCount;
  if (cutoffValue + 1 < MAX_CUTOFF && currentCount >= 12 && nextCount > currentCount) return "similar";

  for (let cutoff = cutoffValue + 1; cutoff <= MAX_CUTOFF; cutoff++) {
    if ((frequencyDistribution[cutoff] ?? 0) > currentCount) return "broaden";
  }
  return "none";
};

export const buildActiveFilters = (
  filters: Omit<TFilters, "ordering"> & {ordering: string | null},
  initialFilters: TInitialFilters,
): TActiveFilter[] => {
  const active: TActiveFilter[] = [];

  if (filters.vibe) {
    active.push({
      count: 1,
      filterKeys: ["vibe"],
      label: "vibe_search",
      tab: "influencer",
      value: filters.vibe,
    });
  }

  if (filters.search) {
    active.push({
      count: 1,
      filterKeys: ["search"],
      label: "influencer_name",
      tab: "influencer",
      value: filters.search,
    });
  }

  if (filters.location_search.length > 0) {
    active.push({
      count: filters.location_search.length,
      filterKeys: ["location_radius_km", "location_search"],
      label: "influencer_region_and_city",
      tab: "influencer",
    });
  }

  if (filters.audience_location_search.length > 0) {
    active.push({
      count: filters.audience_location_search.length,
      filterKeys: ["audience_location_search"],
      label: "audience_region_city",
      tab: "audience",
    });
  }

  if (filters.type !== "ugc") {
    const hasEngagementChanged =
      filters.engagement_start !== initialFilters.engagement__min ||
      filters.engagement_end !== initialFilters.engagement__max;

    if (hasEngagementChanged) {
      active.push({
        count: 1,
        filterKeys: ["engagement_end", "engagement_start"],
        label: "influencer_engagement",
        tab: "influencer",
      });
    }
  }

  if (filters.type === "tiktok") {
    const hasActivityChanged =
      filters.tiktok_last_period_video_count_start !== initialFilters.tiktok_last_period_video_count__min ||
      filters.tiktok_last_period_video_count_end !== initialFilters.tiktok_last_period_video_count__max;

    if (hasActivityChanged) {
      active.push({
        count: 1,
        filterKeys: ["tiktok_last_period_video_count_end", "tiktok_last_period_video_count_start"],
        label: "influencer_activity",
        tab: "influencer",
      });
    }
  }

  if (filters.influencer_age_range_start.length > 0) {
    active.push({
      count: filters.influencer_age_range_start.length,
      filterKeys: ["influencer_age_range_start"],
      label: "influencer_age",
      tab: "influencer",
    });
  }

  if (filters.influencer_interests.length > 0) {
    active.push({
      count: filters.influencer_interests.length,
      filterKeys: ["influencer_interests"],
      label: "influencer_interests",
      tab: "influencer",
    });
  }

  if (filters.gender.length > 0) {
    active.push({
      count: filters.gender.length,
      filterKeys: ["gender"],
      label: "influencer_gender",
      tab: "influencer",
    });
  }

  const clothingCount = filters.clothing_top.length + filters.clothing_below.length + filters.clothing_shoe_size.length;

  if (clothingCount > 0) {
    active.push({
      count: clothingCount,
      filterKeys: ["clothing_below", "clothing_shoe_size", "clothing_top"],
      label: "influencer_clothing",
      tab: "influencer",
    });
  }

  if (filters.award) {
    active.push({count: 1, filterKeys: ["award"], label: "influencer_award", tab: "influencer"});
  }

  if (filters.status.length) {
    active.push({
      count: filters.status.length,
      filterKeys: ["status"],
      label: "influencer_status",
      tab: "influencer",
    });
  }

  if (filters.source) {
    active.push({count: 1, filterKeys: ["source"], label: "common:source", tab: "influencer"});
  }

  if (filters.punishment_type) {
    active.push({count: 1, filterKeys: ["punishment_type"], label: "influencer_punishment", tab: "influencer"});
  }

  if (filters.new_on_platform) {
    active.push({count: 1, filterKeys: ["new_on_platform"], label: "new_on_platform", tab: "influencer"});
  }

  if (filters.group_ids.length > 0) {
    active.push({
      count: filters.group_ids.length,
      filterKeys: ["group_ids", "group_membership"],
      isExclude: filters.group_membership === "not_in_group",
      label: "influencer_groups",
      tab: "influencer",
    });
  }

  if (filters.influencer_country.length > 0) {
    active.push({
      count: filters.influencer_country.length,
      filterKeys: ["influencer_country"],
      label: "influencer_country",
      tab: "influencer",
    });
  }

  const hasFollowersChanged =
    filters.followers_start !== initialFilters.followers__min ||
    filters.followers_end !== initialFilters.followers__max;

  if (hasFollowersChanged) {
    active.push({
      count: 1,
      filterKeys: ["followers_end", "followers_start"],
      label: "influencer_followers",
      tab: "influencer",
    });
  }

  const hasRatingChanged =
    filters.rating_start !== initialFilters.rating__min || filters.rating_end !== initialFilters.rating__max;

  if (hasRatingChanged || filters.new || filters.no_review) {
    active.push({
      count: 1,
      filterKeys: ["new", "no_review", "rating_end", "rating_start"],
      label: "influencer_rating",
      tab: "influencer",
    });
  }

  const hasMedianViewsChanged =
    filters.median_views_start !== initialFilters.median_views__min ||
    filters.median_views_end !== initialFilters.median_views__max;

  if (hasMedianViewsChanged) {
    active.push({
      count: 1,
      filterKeys: ["median_views_end", "median_views_start"],
      label: "influencer_median_views",
      tab: "influencer",
    });
  }

  if (filters.audience_country.length > 0) {
    active.push({
      count: filters.audience_country.length,
      filterKeys: ["audience_country"],
      label: "audience_country",
      tab: "audience",
    });
  }

  if (filters.age_range_start.length > 0) {
    active.push({
      count: filters.age_range_start.length,
      filterKeys: ["age_range_start"],
      label: "audience_age",
      tab: "audience",
    });
  }

  if (filters.audience_gender) {
    active.push({count: 1, filterKeys: ["audience_gender"], label: "audience_gender", tab: "audience"});
  }

  if (filters.is_competitor_filter_active) {
    if (filters.feed_tags.length > 0) {
      active.push({
        count: filters.feed_tags.length,
        filterKeys: ["feed_tags", "feed_tags_mode"],
        isExclude: filters.feed_tags_mode === "exclude",
        label: "feed_tags",
        tab: "pro",
      });
    }

    if (filters.feed_hashtags.length > 0) {
      active.push({
        count: filters.feed_hashtags.length,
        filterKeys: ["feed_hashtags", "feed_hashtags_mode"],
        isExclude: filters.feed_hashtags_mode === "exclude",
        label: "feed_hashtags",
        tab: "pro",
      });
    }
  }

  return active;
};

/**
 * Merges filter sources while preserving session keys (ordering, favourites_first, recommended)
 * and profile-level keys (feed_tags, feed_hashtags, and their modes) from the current state.
 *
 * Use when loading saved filters or resetting — profile-level keys are stored on the
 * company/agency profile, not per-filter, so they should survive these operations.
 */
export const mergeFiltersPreservingCurrent = (
  currentFilters: TFilters,
  ...sources: Array<Partial<TFilters> | undefined>
): TFilters => {
  const merged = Object.assign({}, ...sources);
  for (const key of FILTER_PRESERVED_ON_MERGE_KEYS) {
    merged[key] = currentFilters[key];
  }
  return merged;
};

type TDrawerState = {
  buildSearchParams(): string;
  debouncedPendingSearchParams: {(): void; cancel(): void};
  filters: TFilters;
  isFilterDrawerOpen: boolean;
  pendingSearchParams: string | null;
  savedFilters: TFilters | null;
  setSearchParams(searchParams: string, options?: {skipAnalytics?: boolean}): void;
  updateActiveFilters(): void;
};

type TImmerSet<T> = (updater: T | Partial<T> | ((state: T) => void), shouldReplace?: false, action?: string) => void;

export type TFilterContext = "application" | "group" | "invite" | "network" | "pendingAmbassador";

export const captureFilterAppliedEvents = (
  context: TFilterContext,
  changedFilters: {key: string; value: unknown}[],
): void => {
  if (useUserStore.getState().user.user_type === "admin") return;
  changedFilters.forEach(({key, value}) => {
    capturePostHogEvent(PostHogEvents.InfluencerSearchPerformed, {
      context,
      filterName: key,
      filterValue: typeof value === "object" && value !== null ? JSON.stringify(value) : value,
    });
  });
};

export const captureSearchParamsDiffEvents = (
  oldSearchParams: string | null,
  newSearchParams: string,
  context: TFilterContext,
): void => {
  if (oldSearchParams == null) return;
  const oldParsed = qs.parse(oldSearchParams);
  const newParsed = qs.parse(newSearchParams);
  const keys = new Set([...Object.keys(oldParsed), ...Object.keys(newParsed)]);
  const changed: {key: string; value: unknown}[] = [];
  keys.forEach(key => {
    if (JSON.stringify(oldParsed[key]) === JSON.stringify(newParsed[key])) return;
    changed.push({key, value: newParsed[key]});
  });
  captureFilterAppliedEvents(context, changed);
};

export const createDrawerActions = <TState extends TDrawerState>(
  get: () => TState,
  set: TImmerSet<TState>,
  actionPrefix: string,
) => ({
  applyDrawerFilters: () => {
    get().debouncedPendingSearchParams?.cancel();
    const searchParams = get().buildSearchParams();

    set(
      state => {
        state.isFilterDrawerOpen = false;
        state.pendingSearchParams = null;
        state.savedFilters = null;
      },
      false,
      `${actionPrefix}/applyDrawerFilters`,
    );

    get().setSearchParams(searchParams);
  },
  cancelDrawerFilters: () => {
    get().debouncedPendingSearchParams?.cancel();
    const saved = get().savedFilters;

    if (saved) {
      set(
        state => {
          state.filters = saved;
        },
        false,
        `${actionPrefix}/cancelDrawerFilters/restoreFilters`,
      );
    }

    set(
      state => {
        state.isFilterDrawerOpen = false;
        state.pendingSearchParams = null;
        state.savedFilters = null;
      },
      false,
      `${actionPrefix}/cancelDrawerFilters`,
    );

    get().updateActiveFilters();
  },
  openDrawer: () => {
    set(
      state => {
        state.savedFilters = {...state.filters};
        state.isFilterDrawerOpen = true;
        state.pendingSearchParams = null;
      },
      false,
      `${actionPrefix}/openDrawer`,
    );
  },
});
